import { t } from "@lingui/core/macro";
import { Trans, useLingui } from "@lingui/react/macro";
import { ChatMarkdown } from "@rakazo/chat-ui/web";
import type { ThreadMessage } from "@rakazo/contracts";
import {
  BOT_INSTRUCTIONS_MAX_LENGTH,
  commandVariableName,
  normalizeAskTextAnswer,
} from "@rakazo/contracts";
import { isApprovalAskBlock, isSecretAskBlock, selectedAskActionLabel } from "@rakazo/core";
import { Button, Input, Textarea } from "@rakazo/ui-web";
import type { ComponentProps } from "react";
import { useRef, useState } from "react";
import { rpc } from "../lib/rpc";
import { errorText } from "../lib/user-error";

export type AskBlock = Extract<ThreadMessage["blocks"][number], { kind: "ask" }>;

function formatAnsweredState(
  answer: string | undefined,
  approval: boolean,
  secret: boolean,
  outcome?: "created" | "cancelled",
  actions?: AskBlock["actions"],
  dismissed?: boolean,
): string {
  if (secret) return dismissed ? t`Dismissed` : t`Saved`;
  if (!answer) return t`Answered`;
  if (!approval) return t`Answered: ${selectedAskActionLabel(answer, actions)}`;
  if (outcome === "created") return t`Created`;
  if (outcome === "cancelled") return t`Cancelled`;
  if (answer === "allow") return t`Allowed once`;
  if (answer === "always") return t`Always allowed`;
  if (answer === "deny") return t`Denied`;
  return t`Answered: ${answer}`;
}

function approvalActionLabel(
  id: string,
  fallback: string,
  outcome?: "created" | "cancelled",
  site?: string,
): string {
  if (outcome === "created") return t`Create space`;
  if (outcome === "cancelled") return t`Cancel`;
  if (id === "allow") return t`Allow once`;
  if (id === "always") return site ? t`Always allow ${site}` : t`Always allow this tool`;
  if (id === "deny") return t`Deny`;
  return fallback;
}

type ShortAnswerField = Pick<ComponentProps<"input">, "type" | "inputMode" | "autoComplete">;

/** The browser field that brings up the right keyboard and AutoFill for a short answer. */
function shortAnswerField(format: NonNullable<AskBlock["format"]>): ShortAnswerField {
  if (format === "email") return { type: "email", inputMode: "email", autoComplete: "email" };
  if (format === "phone") return { type: "tel", inputMode: "tel", autoComplete: "tel" };
  if (format === "url") return { type: "url", inputMode: "url", autoComplete: "url" };
  if (format === "date") return { type: "date", autoComplete: "off" };
  if (format === "number") {
    return { type: "text", inputMode: "decimal", autoComplete: "off" };
  }
  return { type: "text", autoComplete: "off" };
}

function siteHost(site: string): string {
  try {
    return new URL(site).host || site;
  } catch {
    return site;
  }
}

function secretFieldLabel(purpose: AskBlock["purpose"]): string {
  if (purpose === "password") return t`Password`;
  if (purpose === "api_key") return t`API key`;
  return t`Code`;
}

export function AskCard({
  block,
  canAnswer,
  canDismiss,
  onAnswer,
  onDismiss,
  onAnswerFiles,
}: {
  block: AskBlock;
  canAnswer: boolean;
  canDismiss: boolean;
  onAnswer: (text: string, username?: string) => Promise<void>;
  /** Turns down a secret request without a value. */
  onDismiss: () => Promise<void>;
  /** Answers a file request with the picked files. */
  onAnswerFiles?: (files: File[]) => Promise<void>;
}) {
  const { t } = useLingui();
  const [editing, setEditing] = useState(false);
  const [answer, setAnswer] = useState(block.instructionUpdate?.after ?? "");
  const [dismissed, setDismissed] = useState(false);
  const [acceptedAnswer, setAcceptedAnswer] = useState<string>();
  const [undone, setUndone] = useState(false);
  const [username, setUsername] = useState("");
  const [pendingAction, setPendingAction] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const submitting = pendingAction !== null;
  const actionInFlight = useRef(false);
  const answered = block.status === "answered" || acceptedAnswer !== undefined || dismissed;
  const displayedAnswer = block.status === "answered" ? block.answer : acceptedAnswer;
  const displayedDismissed = block.status === "answered" ? block.dismissed : dismissed;
  const approvalActions = isApprovalAskBlock(block) ? block.actions : undefined;
  const askActions = block.actions;
  const displayActions = block.instructionUpdate
    ? askActions?.flatMap((action) =>
        action.id === "allow" ? [action, { id: "edit", label: t`Edit` }] : [action],
      )
    : askActions;
  const secretInput = isSecretAskBlock(block);
  // Logins, credentials, page codes, short answers and files act for the run's user, so only they
  // can answer; others see the card without fields or buttons. A connector code stays open.
  const ownerOnly =
    ((secretInput && Boolean(block.credential || block.site)) ||
      ((block.input === "text" || block.input === "file") && !block.approvalEffectId)) &&
    !canDismiss;
  const loginInput = secretInput && block.credential?.auth.type === "login";
  // A one-time code a page sent the person: shown while typed so it can be checked, never saved.
  const pageCodeSite = secretInput && !block.credential ? block.site : undefined;
  // One short value the bot asked for with ask_text; it goes back to the bot as typed.
  const shortAnswerFormat =
    block.input === "text" && !block.approvalEffectId ? (block.format ?? "text") : undefined;
  const fileRequest = block.input === "file" && !block.approvalEffectId;
  const fileInputRef = useRef<HTMLInputElement>(null);

  async function submitFiles(picked: File[]) {
    if (actionInFlight.current || answered || !canAnswer || !onAnswerFiles) return;
    const files = block.multiple ? picked : picked.slice(0, 1);
    if (!files.length) return;
    actionInFlight.current = true;
    setPendingAction("submit");
    setError(null);
    try {
      await onAnswerFiles(files);
      setAcceptedAnswer(files.map((file) => file.name).join(", "));
    } catch (err) {
      setError(errorText(err, t`Could not submit this answer`));
    } finally {
      actionInFlight.current = false;
      setPendingAction(null);
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  }
  const secretLabel = loginInput ? t`Password` : secretFieldLabel(block.purpose);

  async function submitAnswer(value: string) {
    if (actionInFlight.current || answered || !canAnswer) return;
    if (secretInput ? value.length === 0 : !value.trim()) return;
    const submitUsername = loginInput ? username.trim() : undefined;
    if (loginInput && !submitUsername) return;
    const submitValue = secretInput ? value : value.trim();
    actionInFlight.current = true;
    setPendingAction(secretInput ? "submit" : submitValue);
    setError(null);
    if (secretInput) {
      setAnswer("");
      setUsername("");
    }
    try {
      await onAnswer(submitValue, submitUsername);
      setAcceptedAnswer(secretInput ? "" : submitValue);
    } catch (err) {
      // A secret answer's failure may echo the secret, so never show its message.
      setError(
        secretInput
          ? t`Could not submit this answer`
          : errorText(err, t`Could not submit this answer`),
      );
    } finally {
      actionInFlight.current = false;
      setPendingAction(null);
    }
  }

  async function dismissSecret() {
    if (actionInFlight.current || answered || !canAnswer || !canDismiss) return;
    actionInFlight.current = true;
    setPendingAction("dismiss");
    setError(null);
    setAnswer("");
    setUsername("");
    try {
      await onDismiss();
      setDismissed(true);
    } catch {
      setError(t`Could not submit this answer`);
    } finally {
      actionInFlight.current = false;
      setPendingAction(null);
    }
  }

  if (block.instructionUpdate?.undoVersionId)
    return (
      <div className="rounded-2xl border border-border bg-card px-5 py-4">
        <div className="flex flex-wrap items-center gap-2 text-base">
          <Trans>Instructions updated</Trans> ·{" "}
          <Button
            variant="link"
            disabled={undone || submitting}
            className="min-h-11 text-base"
            onClick={async () => {
              if (actionInFlight.current || undone) return;
              actionInFlight.current = true;
              setPendingAction("undo");
              setError(null);
              try {
                await rpc.bots.restoreInstructions({
                  botId: block.instructionUpdate!.botId,
                  versionId: block.instructionUpdate!.undoVersionId!,
                  expectedInstructions: block.instructionUpdate!.after,
                  undo: true,
                });
                setUndone(true);
              } catch (err) {
                setError(errorText(err));
              } finally {
                actionInFlight.current = false;
                setPendingAction(null);
              }
            }}
          >
            {undone ? <Trans>Restored</Trans> : <Trans>Undo</Trans>}
          </Button>
          {error ? (
            <span role="alert" className="text-destructive">
              {error}
            </span>
          ) : null}
        </div>
      </div>
    );

  return (
    <div
      aria-busy={submitting}
      data-testid={
        secretInput
          ? "secret-ask-card"
          : block.instructionUpdate
            ? "instruction-approval-card"
            : undefined
      }
      className={`w-full min-w-0 ${secretInput ? "" : "max-w-xl "}rounded-2xl border border-border bg-card px-5 py-4`}
    >
      <div className="text-base leading-[1.5] text-foreground">
        {block.instructionUpdate ? (
          <Trans>Update instructions?</Trans>
        ) : (
          <ChatMarkdown>{block.text}</ChatMarkdown>
        )}
      </div>
      {secretInput && block.credential ? (
        <div
          data-testid="secret-ask-destination"
          className="mt-2 break-all text-base text-muted-foreground"
        >
          {block.credential.auth.type === "command"
            ? `${t`Command variable`} · $${commandVariableName(block.credential.name)}`
            : block.credential.auth.type === "mcp" && !block.credential.origin
              ? t`MCP server`
              : block.credential.origin}
        </div>
      ) : pageCodeSite ? (
        <div
          data-testid="secret-ask-destination"
          className="mt-2 break-all text-base text-muted-foreground"
        >
          {siteHost(pageCodeSite)}
        </div>
      ) : null}
      {block.detail && !secretInput ? (
        <details className="mt-3">
          <summary className="cursor-pointer text-[13px] text-muted-foreground">
            <Trans>Details</Trans>
          </summary>
          <pre
            className={
              block.detailFormat === "text"
                ? "mt-1 max-h-72 overflow-auto whitespace-pre-wrap break-words font-sans text-base leading-[1.5] text-foreground"
                : "mt-1 max-h-72 overflow-auto whitespace-pre-wrap break-words rounded-xl bg-muted px-3.5 py-3 font-mono text-base leading-[1.7] text-muted-foreground"
            }
          >
            {block.detail}
          </pre>
        </details>
      ) : null}
      {block.instructionUpdate && editing && canAnswer && !answered ? (
        <form
          className="mt-3 space-y-2"
          onSubmit={(event) => {
            event.preventDefault();
            void submitAnswer(JSON.stringify({ instructions: answer }));
          }}
        >
          <Textarea
            className="text-base md:text-base"
            disabled={submitting}
            aria-label={t`Instructions`}
            maxLength={BOT_INSTRUCTIONS_MAX_LENGTH}
            value={answer}
            onChange={(event) => setAnswer(event.target.value)}
          />
          <Button className="min-h-11 text-base" type="submit" disabled={submitting}>
            <Trans>Apply</Trans>
          </Button>
          <Button
            className="min-h-11 text-base"
            type="button"
            variant="outline"
            disabled={submitting}
            onClick={() => setEditing(false)}
          >
            <Trans>Cancel</Trans>
          </Button>
        </form>
      ) : answered ? (
        <div
          role="status"
          className={`mt-3.5 text-base font-medium ${displayedAnswer === "deny" || displayedDismissed ? "text-muted-foreground" : "text-success"}`}
        >
          {block.instructionUpdate
            ? displayedAnswer === "deny"
              ? t`Dismissed`
              : t`Allowed once`
            : formatAnsweredState(
                displayedAnswer,
                Boolean(approvalActions),
                secretInput,
                approvalActions?.find((action) => action.id === displayedAnswer)?.outcome,
                askActions,
                displayedDismissed,
              )}
        </div>
      ) : !canAnswer ? (
        <div className="mt-3.5 text-base font-medium text-muted-foreground">
          <Trans>No longer active</Trans>
        </div>
      ) : ownerOnly ? null : askActions?.length ? (
        <div className={approvalActions ? "mt-3.5 flex flex-wrap gap-2" : "mt-3.5 space-y-1.5"}>
          {displayActions?.map((action) => (
            <Button
              key={action.id}
              variant={approvalActions && action.id === "allow" ? "default" : "outline"}
              className={`h-auto min-h-11 min-w-0 text-base whitespace-normal px-3.5 py-3 text-start font-normal ${approvalActions ? "" : "w-full justify-start border-input bg-secondary"}`}
              disabled={submitting}
              onClick={() =>
                action.id === "edit" && block.instructionUpdate
                  ? setEditing(true)
                  : void submitAnswer(action.id)
              }
            >
              {pendingAction === action.id ? (
                <Trans>Sending…</Trans>
              ) : action.id === "edit" && block.instructionUpdate ? (
                action.label
              ) : approvalActions ? (
                block.instructionUpdate ? (
                  action.id === "allow" ? (
                    t`Apply`
                  ) : (
                    t`Dismiss`
                  )
                ) : (
                  approvalActionLabel(action.id, action.label, action.outcome, block.site)
                )
              ) : (
                action.label
              )}
            </Button>
          ))}
        </div>
      ) : secretInput ? (
        <form
          className="mt-3.5 flex flex-col gap-2"
          onSubmit={(event) => {
            event.preventDefault();
            void submitAnswer(answer);
          }}
        >
          {loginInput ? (
            <Input
              className="min-h-11 w-full text-base md:text-base"
              aria-label={t`Username`}
              autoComplete="off"
              spellCheck={false}
              disabled={submitting}
              value={username}
              onChange={(event) => setUsername(event.target.value)}
              placeholder={t`Username`}
            />
          ) : null}
          {pageCodeSite ? (
            <Input
              className="min-h-11 w-full text-base md:text-base"
              aria-label={secretLabel}
              type="text"
              // Codes can carry letters, so the keyboard stays a text keyboard.
              inputMode="text"
              autoComplete="one-time-code"
              autoCapitalize="off"
              autoCorrect="off"
              spellCheck={false}
              disabled={submitting}
              value={answer}
              onChange={(event) => setAnswer(event.target.value)}
              placeholder={secretLabel}
            />
          ) : (
            <Input
              className="min-h-11 w-full text-base md:text-base"
              aria-label={secretLabel}
              type="password"
              autoComplete="off"
              spellCheck={false}
              disabled={submitting}
              value={answer}
              onChange={(event) => setAnswer(event.target.value)}
              placeholder={secretLabel}
            />
          )}
          <div className="flex gap-2">
            <Button
              type="submit"
              className="h-auto min-h-11 min-w-0 text-base flex-1 whitespace-normal"
              disabled={answer.length === 0 || (loginInput && !username.trim()) || submitting}
            >
              {pendingAction === "submit" ? <Trans>Saving…</Trans> : <Trans>Save</Trans>}
            </Button>
            {canDismiss ? (
              <Button
                type="button"
                variant="outline"
                className="h-auto min-h-11 min-w-0 text-base flex-1 whitespace-normal"
                disabled={submitting}
                onClick={() => void dismissSecret()}
              >
                <Trans>Dismiss</Trans>
              </Button>
            ) : null}
          </div>
        </form>
      ) : fileRequest ? (
        <div className="mt-3.5 flex gap-2">
          <input
            ref={fileInputRef}
            type="file"
            className="hidden"
            data-testid="ask-file-input"
            accept={block.accept}
            multiple={Boolean(block.multiple)}
            onChange={(event) => void submitFiles([...(event.target.files ?? [])])}
          />
          <Button
            type="button"
            className="h-auto min-h-11 min-w-0 text-base flex-1 whitespace-normal"
            disabled={submitting || !onAnswerFiles}
            onClick={() => fileInputRef.current?.click()}
          >
            {pendingAction === "submit" ? <Trans>Sending…</Trans> : <Trans>Attach file</Trans>}
          </Button>
          {canDismiss ? (
            <Button
              type="button"
              variant="outline"
              className="h-auto min-h-11 min-w-0 text-base flex-1 whitespace-normal"
              disabled={submitting}
              onClick={() => void dismissSecret()}
            >
              <Trans>Dismiss</Trans>
            </Button>
          ) : null}
        </div>
      ) : shortAnswerFormat ? (
        <form
          className="mt-3.5 flex flex-col gap-2"
          onSubmit={(event) => {
            event.preventDefault();
            if (normalizeAskTextAnswer(shortAnswerFormat, answer) !== undefined) {
              void submitAnswer(answer);
            }
          }}
        >
          <Input
            className="min-h-11 w-full text-base md:text-base"
            aria-label={t`Answer`}
            {...shortAnswerField(shortAnswerFormat)}
            disabled={submitting}
            value={answer}
            onChange={(event) => setAnswer(event.target.value)}
            placeholder={block.placeholder ?? t`Type your answer`}
          />
          <div className="flex gap-2">
            <Button
              type="submit"
              className="h-auto min-h-11 min-w-0 text-base flex-1 whitespace-normal"
              disabled={
                normalizeAskTextAnswer(shortAnswerFormat, answer) === undefined || submitting
              }
            >
              {submitting ? <Trans>Sending…</Trans> : <Trans>Send answer</Trans>}
            </Button>
            {canDismiss ? (
              <Button
                type="button"
                variant="outline"
                className="h-auto min-h-11 min-w-0 text-base flex-1 whitespace-normal"
                disabled={submitting}
                onClick={() => void dismissSecret()}
              >
                <Trans>Dismiss</Trans>
              </Button>
            ) : null}
          </div>
        </form>
      ) : editing ? (
        <form
          className="mt-3.5 flex flex-col gap-2"
          onSubmit={(event) => {
            event.preventDefault();
            void submitAnswer(answer);
          }}
        >
          <Input
            className="min-h-11 text-base md:text-base"
            disabled={submitting}
            aria-label={t`Answer`}
            value={answer}
            onChange={(event) => setAnswer(event.target.value)}
            placeholder={t`Type your answer`}
          />
          <div className="flex gap-2">
            <Button
              className="min-h-11 text-base"
              type="submit"
              disabled={!answer.trim() || submitting}
            >
              {submitting ? <Trans>Sending…</Trans> : <Trans>Send answer</Trans>}
            </Button>
            <Button
              className="min-h-11 text-base"
              type="button"
              variant="outline"
              disabled={submitting}
              onClick={() => {
                setAnswer("");
                setEditing(false);
              }}
            >
              <Trans>Cancel</Trans>
            </Button>
          </div>
        </form>
      ) : (
        <div className="mt-3.5 flex gap-2">
          <Button
            className="min-h-11 text-base"
            disabled={submitting}
            onClick={() => void submitAnswer("approved")}
          >
            {submitting ? <Trans>Sending…</Trans> : <Trans>Send it</Trans>}
          </Button>
          <Button
            className="min-h-11 text-base"
            variant="outline"
            disabled={submitting}
            onClick={() => setEditing(true)}
          >
            <Trans>Edit first</Trans>
          </Button>
        </div>
      )}
      {error ? (
        <p role="alert" className="mt-3 text-base text-destructive">
          {error}
        </p>
      ) : null}
    </div>
  );
}
